import { PASSWORD_PATTERN, PASSWORD_VALIDATION } from './constants';

/**
 * Generate a create-user / Cognito-safe temporary password.
 * Always includes lower, upper, digit, and special; length >= 12.
 * Guaranteed to pass PASSWORD_PATTERN and PASSWORD_VALIDATION.
 */
export function generateStrongPassword(): string {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const digits = '0123456789';
  // Special set aligned with PASSWORD_VALIDATION (backend / Cognito).
  const special = '!@#%&*-_';
  const all = lower + upper + digits + special;
  const pick = (s: string) => s[Math.floor(Math.random() * s.length)];

  for (let attempt = 0; attempt < 20; attempt += 1) {
    const chars = [
      pick(lower),
      pick(upper),
      pick(digits),
      pick(special),
      ...Array.from({ length: 8 }, () => pick(all)),
    ];
    // Fisher–Yates (reliable shuffle; Array.sort(Math.random) is biased)
    for (let i = chars.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    const password = chars.join('');
    if (meetsPasswordPolicy(password)) {
      return password;
    }
  }

  // Deterministic fallback that always satisfies policy
  return `Aa1!${pick(lower)}${pick(upper)}${pick(digits)}${pick(special)}xY9#`;
}

/** True when password meets app + Cognito create-user policy. */
export function meetsPasswordPolicy(password: string): boolean {
  if (!password || password.length < 12 || /\s/.test(password)) return false;
  // Clone patterns so .test() lastIndex cannot leak across calls
  const pattern = new RegExp(PASSWORD_PATTERN.source, PASSWORD_PATTERN.flags);
  const validation = new RegExp(PASSWORD_VALIDATION.source, PASSWORD_VALIDATION.flags);
  return pattern.test(password) && validation.test(password);
}
